<template>
  <div
    role="main"
    aria-label="Tableau de suivi des commandes pour le support client"
  >
    <b-card class="mb-3 support-filter-card">
      <template #header>
        <h5 class="mb-0">
          Filtres de recherche
        </h5>
      </template>

      <div class="mb-3 mt-1">
        <label class="mr-2 h6">
          Statut logistique :
        </label>
        <div class="status-radio-group d-flex flex-wrap align-items-center">
          <b-form-radio
            v-for="opt in statusOptions"
            :key="opt.value"
            v-model="filters.status"
            :value="opt.value"
            class="mr-2 mb-1"
            @change="onStatusChange"
          >
            {{ opt.text }}
          </b-form-radio>
        </div>
      </div>

      <b-row>
        <b-col md="3">
          <b-form-group
            label="Numéro de bon de livraison (BL)"
            label-for="tracking-bl"
          >
            <b-form-input
              id="tracking-bl"
              v-model="filters.bl"
              placeholder="Saisissez le numéro BL"
              @input="onFilterInput"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group
            label="Nom du destinataire"
            label-for="tracking-recipient"
          >
            <b-form-input
              id="tracking-recipient"
              v-model="filters.recipientName"
              placeholder="Saisissez le nom du destinataire"
              @input="onFilterInput"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group
            label="Téléphone du destinataire"
            label-for="tracking-phone"
          >
            <b-form-input
              id="tracking-phone"
              v-model="filters.recipientPhone"
              placeholder="Saisissez le numéro de téléphone"
              @input="onFilterInput"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group
            label="Montant TTC min (DT)"
            label-for="tracking-amount-min"
          >
            <b-form-input
              id="tracking-amount-min"
              v-model.number="filters.amountMin"
              type="number"
              min="0"
              step="0.001"
              placeholder="0.000"
              @input="onFilterInput"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group
            label="Montant TTC max (DT)"
            label-for="tracking-amount-max"
          >
            <b-form-input
              id="tracking-amount-max"
              v-model.number="filters.amountMax"
              type="number"
              min="0"
              step="0.001"
              placeholder="999.999"
              @input="onFilterInput"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group
            label="Dépôt logistique"
            label-for="tracking-repository"
          >
            <v-select
              id="tracking-repository"
              v-model="filters.repository"
              :options="repositories"
              label="name"
              :reduce="repo => repo.id"
              placeholder="Sélectionner un dépôt logistique"
              :clearable="true"
              :loading="repositoriesLoading"
              @input="onFilterSelectChange"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group
            label="Gouvernorat de destination"
            label-for="tracking-governorate"
          >
            <v-select
              id="tracking-governorate"
              v-model="filters.governorate"
              :options="governorates"
              label="name"
              :reduce="gov => gov.id"
              placeholder="Sélectionner un gouvernorat"
              :clearable="true"
              :loading="governoratesLoading"
              @input="onFilterSelectChange"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group
            label="Fournisseur expéditeur"
            label-for="tracking-supplier"
          >
            <v-select
              id="tracking-supplier"
              v-model="filters.supplier"
              :options="suppliers"
              label="username"
              :reduce="supplier => supplier.id"
              placeholder="Sélectionner un fournisseur"
              :clearable="true"
              :loading="suppliersLoading"
              @input="onFilterSelectChange"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group
            label="Type de colis"
            label-for="tracking-type"
          >
            <b-form-select
              id="tracking-type"
              v-model="filters.parcelType"
              :options="parcelTypeOptions"
              @input="onFilterSelectChange"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group
            label="Fragilité"
            label-for="tracking-fragility"
          >
            <b-form-select
              id="tracking-fragility"
              v-model="filters.fragility"
              :options="fragilityOptions"
              @input="onFilterSelectChange"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group
            label="Synchronisé"
            label-for="tracking-synchronized"
          >
            <b-form-select
              id="tracking-synchronized"
              v-model="filters.synchronized"
              :options="synchronizationOptions"
              @input="onFilterSelectChange"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group
            label="Société d'outsourcing"
            label-for="tracking-outsourcing"
          >
            <v-select
              id="tracking-outsourcing"
              v-model="filters.outsourcingCompany"
              :options="outsourcingCompanies"
              label="name"
              :reduce="company => company.id"
              placeholder="Sélectionner une société"
              :clearable="true"
              :loading="outsourcingLoading"
              @input="onFilterSelectChange"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <div class="d-flex justify-content-end mt-2">
        <b-button
          variant="outline-secondary"
          size="sm"
          @click="resetFilters"
        >
          Réinitialiser
        </b-button>
      </div>
    </b-card>

    <b-card title="Suivi des commandes">
      <b-table
        ref="ordersTable"
        :items="orders"
        :fields="fields"
        :busy="loading"
        responsive
        hover
        small
        show-empty
        primary-key="id"
      >
        <template #table-busy>
          <div class="text-center py-2">
            <b-spinner small />
          </div>
        </template>

        <template #empty>
          <div class="text-center text-muted py-2">
            Aucune commande disponible pour ce périmètre.
          </div>
        </template>

        <template #cell(status_display)="row">
          <b-badge :variant="getStatusVariant(row.item.status)">
            {{ row.item.status_display }}
          </b-badge>
        </template>

        <template #cell(zone)="row">
          <div v-if="row.item.governorate">
            {{ row.item.governorate }}
          </div>
          <small
            v-if="row.item.delegation"
            class="text-muted"
          >
            {{ row.item.delegation }}
          </small>
        </template>

        <template #cell(phone1)="row">
          {{ row.item.phone1 || '-' }}
        </template>

        <template #cell(product)="row">
          <span v-if="row.item.products && row.item.products.length">
            {{ row.item.products[0].product || row.item.products[0].designation || row.item.products[0].name || '-' }}
          </span>
          <span v-else>-</span>
        </template>

        <template #cell(repository)="row">
          {{ row.item.repository ? row.item.repository.name : '-' }}
        </template>

        <template #cell(supplier)="row">
          {{ row.item.supplier ? row.item.supplier.username : '-' }}
        </template>

        <template #cell(price_ttc)="row">
          {{ row.item.price_ttc ? Number(row.item.price_ttc).toFixed(3) : '-' }}
        </template>

        <template #cell(return_number)="row">
          {{ row.item.return_number || 0 }}
        </template>

        <template #cell(created_at)="row">
          {{ formatDate(row.item.created_at) }}
        </template>

        <template #cell(delivered_at)="row">
          {{ formatDate(row.item.delivered_at) }}
        </template>

        <template #cell(central_hub_received_at)="row">
          {{ formatDate(row.item.central_hub_received_at) }}
        </template>

        <template #cell(outsourcing_company_name)="row">
          {{ row.item.outsourcing_company_name || '-' }}
        </template>

        <template #cell(actions)="row">
          <b-button
            size="sm"
            variant="outline-primary"
            class="mr-1"
            @click="row.toggleDetails"
          >
            Historique
          </b-button>
          <b-button
            size="sm"
            variant="primary"
            :to="{ name: 'order-show', params: { id: row.item.id } }"
          >
            Voir
          </b-button>
        </template>

        <template #row-details="row">
          <div class="px-1 py-1 bg-light">
            <div class="font-weight-bold mb-1">
              Historique de suivi
            </div>
            <div v-if="!row.item.tracking_history || row.item.tracking_history.length === 0">
              Aucun historique disponible.
            </div>
            <ul
              v-else
              class="pl-2 mb-0"
            >
              <li
                v-for="history in row.item.tracking_history"
                :key="history.id"
                class="mb-1"
              >
                <strong>{{ formatDate(history.created_at) }}</strong>
                - {{ history.message }}
                <span v-if="history.done_by"> ({{ history.done_by }})</span>
                <div
                  v-if="history.description"
                  class="text-muted"
                >
                  {{ history.description }}
                </div>
              </li>
            </ul>
          </div>
        </template>
      </b-table>

      <div class="d-flex justify-content-end mt-2">
        <b-pagination
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
          first-number
          last-number
          @input="fetchOrders"
        />
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BBadge,
  BButton,
  BCard,
  BCol,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormRadio,
  BPagination,
  BRow,
  BSpinner,
  BTable,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import axios from 'axios'
import store from '@/store/store'

export default {
  name: 'SupportTracking',
  components: {
    BBadge,
    BButton,
    BCard,
    BCol,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormRadio,
    BPagination,
    BRow,
    BSpinner,
    BTable,
    vSelect,
  },
  data() {
    return {
      loading: false,
      repositoriesLoading: false,
      repositories: [],
      governoratesLoading: false,
      governorates: [],
      suppliersLoading: false,
      suppliers: [],
      outsourcingLoading: false,
      outsourcingCompanies: [],
      orders: [],
      filterDebounceTimeout: null,
      currentPage: 1,
      perPage: 20,
      totalRows: 0,
      statusOptions: [
        { value: null, text: 'Tous les statuts' },
        { value: 0, text: 'En attente' },
        { value: 15, text: 'En attente de collecte' },
        { value: 1, text: 'En Pickup' },
        { value: 2, text: 'Arrivé au dépôt' },
        { value: 5, text: 'Retour au dépôt' },
        { value: 3, text: 'En cours' },
        { value: 20, text: 'Problème livraison' },
        { value: 22, text: 'Retour définitif' },
        { value: 23, text: 'Relance' },
        { value: 24, text: 'Anomalie de livraison' },
        { value: 4, text: 'Livré au destinataire' },
        { value: 6, text: 'Livré et payé' },
        { value: 7, text: 'Retour expéditeur' },
        { value: 10, text: 'Rtn client-agence' },
        { value: 11, text: 'Rtn client-agence reçu' },
        { value: 9, text: 'Retour payé' },
        { value: 18, text: 'Rendez-vous programmé' },
        { value: 19, text: 'Livré Temporaire' },
      ],
      filters: {
        status: null,
        bl: '',
        recipientName: '',
        recipientPhone: '',
        amountMin: '',
        amountMax: '',
        repository: null,
        governorate: null,
        supplier: null,
        parcelType: '',
        fragility: '',
        synchronized: '',
        outsourcingCompany: null,
      },
      fields: [
        { key: 'id', label: 'BL', sortable: true },
        { key: 'name', label: 'Client', sortable: true },
        { key: 'supplier', label: 'Fournisseur', sortable: true },
        { key: 'zone', label: 'Zone / Adresse', sortable: false },
        { key: 'phone1', label: 'Téléphone', sortable: true },
        { key: 'product', label: 'Produit', sortable: false },
        { key: 'status_display', label: 'Etat', sortable: true },
        { key: 'price_ttc', label: 'Prix TTC', sortable: true },
        { key: 'return_number', label: 'N°retour', sortable: true },
        { key: 'created_at', label: 'Date création', sortable: true },
        { key: 'delivered_at', label: 'Date Livraison', sortable: true },
        { key: 'central_hub_received_at', label: 'Date Réception', sortable: true },
        { key: 'outsourcing_company_name', label: 'Synchronisé avec', sortable: true },
        { key: 'actions', label: 'Actions', sortable: false },
      ],
      parcelTypeOptions: [
        { value: '', text: 'Tous les types' },
        { value: 'normal', text: 'Normal' },
        { value: 'exchange', text: 'Échange' },
      ],
      fragilityOptions: [
        { value: '', text: 'Tous les colis' },
        { value: 'fragile', text: 'Fragile' },
        { value: 'notFragile', text: 'Non fragile' },
      ],
      synchronizationOptions: [
        { value: '', text: 'Tous les statuts' },
        { value: 'yes', text: 'Synchronisé' },
        { value: 'no', text: 'Non synchronisé' },
      ],
    }
  },
  computed: {
    supportClientId() {
      return store.state.user?.id || localStorage.getItem('id')
    },
  },
  created() {
    this.fetchRepositories()
    this.fetchGovernorates()
    this.fetchSuppliers()
    this.fetchOutsourcingCompanies()
    this.fetchOrders()
  },
  beforeDestroy() {
    if (this.filterDebounceTimeout) {
      clearTimeout(this.filterDebounceTimeout)
    }
  },
  methods: {
    formatDate(value) {
      if (!value) return '-'
      return new Date(value).toLocaleString('fr-FR')
    },
    getStatusVariant(status) {
      const map = {
        0: 'light-warning', // En attente
        1: 'light-info', // En Pickup
        2: 'light-info', // Arrivé au dépôt
        3: 'light-primary', // En cours
        4: 'light-success', // Livré au destinataire
        5: 'light-warning', // Retour au dépôt
        6: 'light-success', // Livré et payé
        7: 'light-danger', // Retour expéditeur
        9: 'light-success', // Retour payé
        10: 'light-warning', // Rtn client-agence
        11: 'light-warning', // Rtn client-agence reçu
        15: 'light-info', // En attente de collecte
        18: 'light-info', // Rendez-vous programmé
        19: 'light-warning', // Livré Temporaire
        20: 'light-danger', // Problème livraison
        22: 'light-danger', // Retour définitif
        23: 'light-warning', // Relance
        24: 'light-danger', // Anomalie de livraison
      }
      return map[status] || 'light-secondary'
    },
    async fetchRepositories() {
      this.repositoriesLoading = true
      try {
        const { data } = await axios.get('/api/repositories/')
        this.repositories = data.results || data
      } catch (error) {
        this.repositories = []
      } finally {
        this.repositoriesLoading = false
      }
    },
    async fetchGovernorates() {
      this.governoratesLoading = true
      try {
        const { data } = await axios.get('/api/governorates/')
        this.governorates = data.results || data
      } catch (error) {
        this.governorates = []
      } finally {
        this.governoratesLoading = false
      }
    },
    async fetchSuppliers() {
      this.suppliersLoading = true
      try {
        const { data } = await axios.get('/api/auth/users/suppliers/?page_size=1000')
        this.suppliers = data.results || data
      } catch (error) {
        this.suppliers = []
      } finally {
        this.suppliersLoading = false
      }
    },
    async fetchOutsourcingCompanies() {
      this.outsourcingLoading = true
      try {
        const { data } = await axios.get('/api/companies/outsourcing-company/')
        this.outsourcingCompanies = data.results || data
      } catch (error) {
        this.outsourcingCompanies = []
      } finally {
        this.outsourcingLoading = false
      }
    },
    buildQueryParams() {
      const params = {
        page: this.currentPage,
        page_size: this.perPage,
        supportClientId: this.supportClientId,
      }

      if (this.filters.bl) params.bl = this.filters.bl.trim()
      if (this.filters.status !== null) params.status = this.filters.status
      if (this.filters.repository) params.repository = this.filters.repository
      if (this.filters.recipientName) params.recipient_name = this.filters.recipientName
      if (this.filters.recipientPhone) params.phone = this.filters.recipientPhone
      if (this.filters.amountMin !== '' && this.filters.amountMin !== null) {
        params.price_ttc_min = this.filters.amountMin
      }
      if (this.filters.amountMax !== '' && this.filters.amountMax !== null) {
        params.price_ttc_max = this.filters.amountMax
      }
      if (this.filters.governorate) params.governorate = this.filters.governorate
      if (this.filters.supplier) params.supplier = this.filters.supplier
      if (this.filters.parcelType) params.type = this.filters.parcelType
      if (this.filters.fragility) params.fragility = this.filters.fragility
      if (this.filters.synchronized) params.synchronized = this.filters.synchronized
      if (this.filters.outsourcingCompany) {
        params.outsourcing_company = this.filters.outsourcingCompany
      }

      return params
    },
    async fetchOrders() {
      this.loading = true
      try {
        const { data } = await axios.get('/api/orders/', {
          params: this.buildQueryParams(),
        })
        this.orders = data.results || data
        this.totalRows = data.count || this.orders.length
      } catch (error) {
        this.orders = []
        this.totalRows = 0
      } finally {
        this.loading = false
      }
    },
    scheduleFetch(delay = 350) {
      if (this.filterDebounceTimeout) {
        clearTimeout(this.filterDebounceTimeout)
      }
      this.filterDebounceTimeout = setTimeout(() => {
        this.fetchOrders()
      }, delay)
    },
    onFilterInput() {
      this.currentPage = 1
      this.scheduleFetch()
    },
    onFilterSelectChange() {
      this.currentPage = 1
      this.fetchOrders()
    },
    onStatusChange(value) {
      this.filters.status = value
      this.currentPage = 1
      this.$nextTick(() => {
        this.fetchOrders()
      })
    },
    resetFilters() {
      this.filters = {
        status: null,
        bl: '',
        recipientName: '',
        recipientPhone: '',
        amountMin: '',
        amountMax: '',
        repository: null,
        governorate: null,
        supplier: null,
        parcelType: '',
        fragility: '',
        synchronized: '',
        outsourcingCompany: null,
      }
      this.currentPage = 1
      this.fetchOrders()
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@core/scss/vue/libs/vue-select.scss';

.support-filter-card {
  background: #f6f8fb;
  border-color: #ebedef;
}

.status-radio-group {
  gap: 0.75rem;
}
</style>
